import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu, X, Home, Calendar, FolderKanban, Camera, GraduationCap, ShoppingBag,
  MessageCircle, FileText, ClipboardList, ShieldAlert, LayoutGrid,
  Shield, ChevronRight, Users as UsersIcon, User,
} from "lucide-react";
import { useLang } from "../doserne/i18n";

// Lightweight top navigation bar for the admin Capacity / Project Overview
// pages. Mirrors the Doserne menu routes but WITHOUT the heavy DoserneShell
// (no polling, chat or push) so it stays cheap to mount. Labels are pulled
// from the shared i18n dictionary so they translate in every language.
const NAV = [
  { id: "home", icon: Home, to: "/doserne", tkey: "nav.home" },
  { id: "plan", icon: Calendar, to: "/doserne/plan", tkey: "nav.daily_plan" },
  { id: "projects", icon: FolderKanban, to: "/doserne/prosjekter", tkey: "nav.projects" },
  { id: "progress", icon: Camera, to: "/doserne/progresjon", tkey: "nav.progress" },
  { id: "colleagues", icon: UsersIcon, to: "/doserne/kolleger", tkey: "nav.colleagues" },
  { id: "chat", icon: MessageCircle, to: "/doserne/chat", tkey: "nav.chat" },
  { id: "kurs", icon: GraduationCap, to: "/doserne/kurs", tkey: "nav.kurs" },
  { id: "klaer", icon: ShoppingBag, to: "/doserne/klaer", tkey: "nav.klaer" },
  { id: "capacity", icon: LayoutGrid, to: "/admin/project-overview", tkey: "nav.capacity" },
  { id: "underlag", icon: FileText, to: "/underlag", tkey: "nav.underlag" },
  { id: "kontroll", icon: ClipboardList, to: "/apps/kontroll", tkey: "nav.kontroll" },
  { id: "sja", icon: ShieldAlert, to: "/apps/sja", tkey: "nav.sja" },
  { id: "profile", icon: User, to: "/doserne/profil", tkey: "nav.profile" },
];

function Logo() {
  return (
    <div className="inline-flex items-center gap-2">
      <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center shadow-sm">
        <Shield className="w-5 h-5 text-cyan-400" strokeWidth={2.5} />
      </div>
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Doserne</span>
        <span className="text-lg font-black text-slate-900 -mt-0.5">Academy</span>
      </div>
    </div>
  );
}

export default function DoserneTopNav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, [open]);

  return (
    <>
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}>
        <div className="max-w-full mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => setOpen(true)} data-testid="topnav-meny"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 active:bg-cyan-700 text-white shadow-sm">
            <Menu className="w-5 h-5" strokeWidth={2.5} />
            <span className="text-xs font-black uppercase tracking-widest">Meny</span>
          </button>
          <Link to="/doserne" className="flex-1" data-testid="topnav-logo"><Logo /></Link>
        </div>
      </header>

      <div onClick={() => setOpen(false)} data-testid="topnav-backdrop"
           className={`fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}/>
      <aside data-testid="topnav-drawer"
             className={`fixed top-0 left-0 z-50 h-full w-[85vw] max-w-[320px] bg-white shadow-2xl transform transition-transform duration-300 ease-out flex flex-col ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <Logo />
          <button onClick={() => setOpen(false)} data-testid="topnav-close" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            const cls = `flex items-center gap-3 px-5 py-3 text-sm font-medium transition ${
              active ? "bg-cyan-50 text-cyan-700 border-r-4 border-cyan-500" : "text-slate-700 hover:bg-slate-50 active:bg-slate-100"
            }`;
            return (
              <Link key={item.id} to={item.to} className={cls} data-testid={`topnav-link-${item.id}`}>
                <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.8} />
                <span className="flex-1">{item.tkey ? t(item.tkey) : item.label}</span>
                {active && <ChevronRight className="w-4 h-4 text-cyan-500" />}
              </Link>
            );
          })}
          <Link to="/admin/permissions" className="flex items-center gap-3 px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 border-t border-slate-100 mt-2" data-testid="topnav-link-admin">
            <LayoutGrid className="w-5 h-5" strokeWidth={1.8} />
            <span className="flex-1">Admin</span>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </Link>
        </nav>
      </aside>
    </>
  );
}
